export type RandomSelectionStrategy = 'on-complete' | 'on-state-enter';

export interface WeightedActionItem {
  animation?: string;
  weight?: number;
  loop?: boolean;
  interval?: number;
  [key: string]: any;
}

export interface StateGroupConfig {
  strategy?: RandomSelectionStrategy;
  interval?: number; // duration in seconds (for image rotation or timers)
  items: WeightedActionItem[];
}

export type StateMappingItem =
  | string
  | WeightedActionItem
  | WeightedActionItem[]
  | StateGroupConfig;

/**
 * Normalizes any legacy string, single-object, array-of-items, or full config object
 * into a standardized `StateGroupConfig`.
 */
export function normalizeStateGroupConfig(
  raw?: StateMappingItem | null,
  defaultStrategy: RandomSelectionStrategy = 'on-complete',
  defaultInterval: number = 5,
  defaultLoop?: boolean
): StateGroupConfig {
  if (!raw) {
    return {
      strategy: defaultStrategy,
      interval: defaultInterval,
      items: [],
    };
  }

  // 1. Raw string (e.g. "idle" or "character_idle.png")
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return {
      strategy: defaultStrategy,
      interval: defaultInterval,
      items: trimmed
        ? [
            {
              animation: trimmed,
              weight: 1,
              loop: defaultLoop !== undefined ? defaultLoop : true,
            },
          ]
        : [],
    };
  }

  // 2. Array of WeightedActionItem or string
  if (Array.isArray(raw)) {
    const items: WeightedActionItem[] = [];
    for (const item of raw as (WeightedActionItem | string)[]) {
      if (typeof item === 'string') {
        const trimmed = (item as string).trim();
        if (trimmed) {
          items.push({
            animation: trimmed,
            weight: 1,
            loop: defaultLoop !== undefined ? defaultLoop : true,
          });
        }
      } else if (item && typeof item === 'object') {
        const anim = (item.animation || '').trim();
        if (anim) {
          items.push({
            ...item,
            animation: anim,
            weight: typeof item.weight === 'number' && item.weight > 0 ? item.weight : 1,
            loop: item.loop !== undefined ? item.loop : (defaultLoop !== undefined ? defaultLoop : true),
          });
        }
      }
    }

    return {
      strategy: defaultStrategy,
      interval: defaultInterval,
      items,
    };
  }

  // 3. Full StateGroupConfig with items property
  if (typeof raw === 'object' && 'items' in raw && Array.isArray((raw as StateGroupConfig).items)) {
    const group = raw as StateGroupConfig;
    const items: WeightedActionItem[] = [];
    for (const item of group.items as (WeightedActionItem | string)[]) {
      if (typeof item === 'string') {
        const trimmed = (item as string).trim();
        if (trimmed) {
          items.push({
            animation: trimmed,
            weight: 1,
            loop: defaultLoop !== undefined ? defaultLoop : true,
          });
        }
      } else if (item && typeof item === 'object') {
        const anim = (item.animation || '').trim();
        if (anim) {
          items.push({
            ...item,
            animation: anim,
            weight: typeof item.weight === 'number' && item.weight > 0 ? item.weight : 1,
            loop: item.loop !== undefined ? item.loop : (defaultLoop !== undefined ? defaultLoop : true),
          });
        }
      }
    }

    return {
      strategy: group.strategy || defaultStrategy,
      interval: typeof group.interval === 'number' && group.interval > 0 ? group.interval : defaultInterval,
      items,
    };
  }

  // 4. Legacy single object without items (e.g. { animation: 'idle', loop: true })
  if (typeof raw === 'object') {
    const single = raw as WeightedActionItem;
    const anim = single.animation?.trim() || '';
    return {
      strategy: defaultStrategy,
      interval: typeof single.interval === 'number' && single.interval > 0 ? single.interval : defaultInterval,
      items: anim
        ? [
            {
              ...single,
              animation: anim,
              weight: typeof single.weight === 'number' && single.weight > 0 ? single.weight : 1,
              loop: single.loop !== undefined ? single.loop : (defaultLoop !== undefined ? defaultLoop : true),
            },
          ]
        : [],
    };
  }

  return {
    strategy: defaultStrategy,
    interval: defaultInterval,
    items: [],
  };
}

/**
 * Calculates normalized percentage distribution for an array of weighted items.
 */
export function calculateNormalizedWeights(
  items: WeightedActionItem[]
): { item: WeightedActionItem; percentage: number }[] {
  if (!items || items.length === 0) return [];

  const validWeights = items.map((it) => (typeof it.weight === 'number' && it.weight > 0 ? it.weight : 1));
  const totalWeight = validWeights.reduce((sum, w) => sum + w, 0);

  if (totalWeight <= 0) {
    const equalShare = Math.round(100 / items.length);
    return items.map((item) => ({ item, percentage: equalShare }));
  }

  return items.map((item, idx) => {
    const w = validWeights[idx];
    const percentage = Math.round((w / totalWeight) * 100);
    return { item, percentage };
  });
}

/**
 * Picks a random item based on positive probability weights.
 */
export function pickWeightedItem<T extends { weight?: number }>(
  items: T[],
  randomFn: () => number = Math.random
): T | null {
  if (!items || items.length === 0) return null;
  if (items.length === 1) return items[0];

  const weights = items.map((it) => (typeof it.weight === 'number' && it.weight > 0 ? it.weight : 1));
  const totalWeight = weights.reduce((acc, curr) => acc + curr, 0);

  if (totalWeight <= 0) {
    const randomIdx = Math.floor(randomFn() * items.length);
    return items[randomIdx] || null;
  }

  const threshold = randomFn() * totalWeight;
  let cumulative = 0;

  for (let i = 0; i < items.length; i++) {
    cumulative += weights[i];
    if (threshold < cumulative || i === items.length - 1) {
      return items[i];
    }
  }

  return items[0] || null;
}

/**
 * Serializes a StateGroupConfig into a clean StateMappingItem for extension.json.
 */
export function stateGroupConfigToManifestItem(
  config: StateGroupConfig,
  defaultStrategy: RandomSelectionStrategy = 'on-complete',
  defaultInterval: number = 5
): StateMappingItem | undefined {
  if (!config.items || config.items.length === 0) {
    return undefined;
  }

  const validItems = config.items.filter(
    (it) => it && typeof it.animation === 'string' && it.animation.trim() !== ''
  );
  if (validItems.length === 0) {
    return undefined;
  }

  const hasCustomStrategy = config.strategy && config.strategy !== defaultStrategy;
  const hasCustomInterval = config.interval && config.interval !== defaultInterval;

  // If there's custom strategy or interval, output full StateGroupConfig
  if (hasCustomStrategy || hasCustomInterval) {
    return {
      strategy: config.strategy || defaultStrategy,
      ...(hasCustomInterval ? { interval: config.interval } : {}),
      items: validItems,
    };
  }

  // If only 1 item and default weight
  if (validItems.length === 1) {
    const single = validItems[0];
    if ((single.weight === undefined || single.weight === 1) && !single.interval) {
      if (single.loop === undefined) {
        return single.animation;
      }
      return { animation: single.animation, loop: single.loop };
    }
    return [single];
  }

  // Multiple items with default strategy and interval -> clean array
  return validItems;
}
